import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { FREQUENCIA_LABELS } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais, type ResultadoDoEnvio } from "@/lib/dinheiro";
import { mesDoExtrato } from "@/components/financeiro/extratos/situacao";
import { LinhaDoEnvio, ResultadoDoEnvioLinha } from "./LinhaDoEnvio";

const chave = (d: { clientOrganizationId: string; dentistaId: string | null }) => `${d.clientOrganizationId}|${d.dentistaId ?? ""}`;
const secao = "overflow-hidden rounded-lg border border-zinc-200 dark:border-border";
const titulo = "border-b border-zinc-100 bg-zinc-50/60 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:border-border dark:bg-muted/30 dark:text-muted-foreground";

/** Fechar e enviar: emite o extrato de quem tem item pronto e manda por e-mail, junto com os
 *  emitidos que ainda não foram. Quem tem item sem preço vem desmarcado. */
export function FecharEEnviarDialog({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const preparo = trpc.financeiro.envio.preparar.useQuery(undefined, { enabled: aberto }).data;
  const [emitir, setEmitir] = useState<Set<string>>(new Set());
  const [enviar, setEnviar] = useState<Set<string>>(new Set());
  const [resultados, setResultados] = useState<ResultadoDoEnvio[] | null>(null);

  useEffect(() => {
    if (!aberto || !preparo) return;
    setEmitir(new Set(preparo.aEmitir.filter((d) => d.semPreco === 0).map(chave)));
    setEnviar(new Set(preparo.aEnviar.map((e) => e.id)));
  }, [aberto, preparo]);
  useEffect(() => {
    if (!aberto) setResultados(null);
  }, [aberto]);

  const mutacao = trpc.financeiro.envio.fecharEEnviar.useMutation({
    onSuccess: (r) => {
      setResultados(r.resultados);
      toast.success(`${r.emitidos} ${r.emitidos === 1 ? "extrato emitido" : "extratos emitidos"}, ${r.enviados} ${r.enviados === 1 ? "enviado" : "enviados"}.`);
      utils.financeiro.invalidate();
      utils.extratoSimples.invalidate();
      utils.partner.resumo.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const marcar = (set: typeof setEmitir, k: string, v: boolean) =>
    set((atual) => {
      const novo = new Set(atual);
      if (v) novo.add(k);
      else novo.delete(k);
      return novo;
    });
  const aEmitir = preparo?.aEmitir ?? [];
  const aEnviar = preparo?.aEnviar ?? [];
  const total = aEmitir.filter((d) => emitir.has(chave(d))).reduce((t, d) => t + d.prontoCents, 0) + aEnviar.filter((e) => enviar.has(e.id)).reduce((t, e) => t + e.totalCents, 0);
  const qtd = emitir.size + enviar.size;

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="flex max-h-[90dvh] max-w-2xl flex-col gap-4 overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle>Fechar e enviar</DialogTitle>
          <DialogDescription>
            {resultados ? "Como ficou cada extrato." : "Emite com o que já fechou de cada cliente e manda por e-mail, com o link do extrato. Desmarque quem não quiser agora."}
          </DialogDescription>
        </DialogHeader>

        {resultados ? (
          <ul className={`${secao} divide-y divide-zinc-100 dark:divide-border/50`}>
            {resultados.map((r, i) => (
              <ResultadoDoEnvioLinha key={i} r={r} />
            ))}
          </ul>
        ) : !preparo ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
          </div>
        ) : aEmitir.length + aEnviar.length === 0 ? (
          <p className="py-8 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nada pronto para enviar agora.</p>
        ) : (
          <>
            {aEmitir.length > 0 && (
              <section className={secao}>
                <p className={titulo}>Emitir e enviar</p>
                <ul className="divide-y divide-zinc-100 dark:divide-border/50">
                  {aEmitir.map((d) => (
                    <LinhaDoEnvio
                      key={chave(d)}
                      marcado={emitir.has(chave(d))}
                      onMarcar={(v) => marcar(setEmitir, chave(d), v)}
                      nome={d.nome}
                      detalhe={`${d.dentistaId ? `${d.clinica} · ` : ""}${d.prontos} ${d.prontos === 1 ? "item pronto" : "itens prontos"} · ${FREQUENCIA_LABELS[d.frequencia]}`}
                      valorCents={d.prontoCents}
                      email={d.email}
                      partnerId={d.partnerId}
                      aviso={d.semPreco ? `${d.semPreco} ${d.semPreco === 1 ? "item sem preço" : "itens sem preço"}: confira antes em A faturar.` : null}
                    />
                  ))}
                </ul>
              </section>
            )}
            {aEnviar.length > 0 && (
              <section className={secao}>
                <p className={titulo}>Emitidos e ainda não enviados</p>
                <ul className="divide-y divide-zinc-100 dark:divide-border/50">
                  {aEnviar.map((e) => (
                    <LinhaDoEnvio
                      key={e.id}
                      marcado={enviar.has(e.id)}
                      onMarcar={(v) => marcar(setEnviar, e.id, v)}
                      nome={e.destinatarioNome}
                      detalhe={`Nº ${e.numero} · mês ${mesDoExtrato(e.rotulo)}${e.dentistaId && e.clinica ? ` · ${e.clinica}` : ""}`}
                      valorCents={e.totalCents}
                      email={e.email}
                      partnerId={e.partnerId}
                    />
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        <div className="sticky -bottom-6 -mx-6 -mb-6 flex items-center gap-3 border-t border-zinc-200 bg-white px-6 py-4 dark:border-border dark:bg-card">
          {!resultados && (
            <p className="text-sm text-zinc-600 dark:text-muted-foreground">
              {qtd} {qtd === 1 ? "extrato" : "extratos"} · <span className="font-bold tabular-nums text-zinc-900 dark:text-foreground">{reais(total)}</span>
            </p>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onFechar} className="h-10 rounded-md border border-zinc-200 px-4 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground">
              {resultados ? "Fechar" : "Cancelar"}
            </button>
            {!resultados && (
              <button
                type="button"
                disabled={qtd === 0 || mutacao.isPending}
                onClick={() =>
                  mutacao.mutate({
                    emitir: aEmitir.filter((d) => emitir.has(chave(d))).map((d) => ({ clientOrganizationId: d.clientOrganizationId, dentistaId: d.dentistaId })),
                    enviar: [...enviar],
                  })
                }
                className="inline-flex h-10 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
              >
                {mutacao.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {emitir.size ? `Emitir ${emitir.size} e enviar` : `Enviar ${enviar.size}`}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
